import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { Task } from '../../contexts/TasksContext'
import { resetState } from '../actions'

export interface TasksListeningScope {
    type: 'project' | 'plate' | 'workspace'
    projectId?: string
    workspaceId: string
    userId?: string
}

interface TasksState {
    byId: Record<string, Task>
    allIds: string[]
    optimistic: Record<string, Task>
    loading: boolean
    initialized: boolean
    // True once the deferred completed-tasks listener has delivered its first
    // snapshot. The open listener sets `initialized`; completed tasks arrive a
    // beat later, so surfaces that must account for completed tasks (e.g. a
    // deep link to a completed task) wait on this before concluding "not found".
    completedInitialized: boolean
    // True while the deep-link direct fetch (fetchTaskByNumber) is in flight. Lets the task
    // modal route open a directly-linked task without waiting for the full workspace load, and
    // conclude "not found" only once this fetch has finished (SCD-887).
    deepLinkFetchPending: boolean
    error: string | null
}

const initialState: TasksState = {
    byId: {},
    allIds: [],
    optimistic: {},
    loading: false,
    initialized: false,
    completedInitialized: false,
    deepLinkFetchPending: false,
    error: null,
}

const tasksSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        // Saga triggers
        startListening: (_state, _action: PayloadAction<TasksListeningScope>) => {},
        stopListening: () => {},
        createTaskSaga: (_state, _action: PayloadAction<{
            listId: string
            name: string
            projectId: string
            workspaceId: string
            userId: string
            description?: unknown
        }>) => {},
        // Batch create — one task per name, in a single counter transaction (SCD-897: paste a
        // bullet list into the add-task input). Separate from createTaskSaga because that one is
        // takeLatest (rapid single dispatches would cancel each other) and shares a millisecond
        // tempId; this creates all names at once with incrementing order/number.
        createTasksSaga: (_state, _action: PayloadAction<{
            names: string[]
            listId: string
            projectId: string
            workspaceId: string
            userId: string
        }>) => {},
        updateTaskSaga: (_state, _action: PayloadAction<{ taskId: string; data: Partial<Task> }>) => {},
        // Description saves are separate from updateTaskSaga on purpose. updateTaskSaga is
        // takeLatest, so a field update dispatched right after a description edit (e.g. changing
        // the assignee just after typing) would CANCEL the still-running description worker
        // mid-write — the async setDoc to task_descriptions never completes and the body is lost
        // (SCD-905). This action has its own takeEvery worker, so it can never be cancelled by a
        // later task-doc update. (Same reason createTasksSaga was split out — see above.)
        updateTaskDescriptionSaga: (_state, _action: PayloadAction<{ taskId: string; description: unknown }>) => {},
        deleteTaskSaga: (_state, _action: PayloadAction<string>) => {},
        reorderTaskSaga: (_state, _action: PayloadAction<{ taskId: string; newOrder: number }>) => {},
        moveTaskToListSaga: (_state, _action: PayloadAction<{ taskId: string; targetListId: string; newOrder: number }>) => {},
        toggleCompleteSaga: (_state, _action: PayloadAction<{ taskId: string; isCompleted: boolean }>) => {},
        // Task descriptions live in a separate `task_descriptions` collection and are
        // loaded lazily only when a task is opened (keeps them out of the eager workspace
        // tasks snapshot). The loaded content is merged into byId[taskId].description.
        subscribeDescriptionSaga: (_state, _action: PayloadAction<string>) => {},
        unsubscribeDescriptionSaga: () => {},
        // One-shot prefetch of a task's description (e.g. on hover in the list), so opening the
        // task shows it immediately instead of after a cold Firestore read. By default a no-op if
        // already loaded (idle warming). `force: true` (deliberate intent — hover/mousedown) ignores
        // the cached value and re-reads from the server, so a description that changed after the tab
        // loaded is refreshed (and the IndexedDB cache warmed) before the modal opens — a description
        // edit doesn't touch the task doc, so an open tab has no other signal it changed (SCD-927).
        prefetchDescriptionSaga: (_state, _action: PayloadAction<string | { taskId: string; force?: boolean }>) => {},
        // Deep-link fast path (SCD-887): fetch one task by its number directly so a /task/PREFIX-N
        // link opens without waiting for the full workspace task wave. Sets pending true here; the
        // worker clears it when done.
        fetchTaskByNumberSaga: (state, _action: PayloadAction<{ workspaceId: string; number: number }>) => {
            state.deepLinkFetchPending = true
        },
        setDeepLinkFetchPending: (state, action: PayloadAction<boolean>) => {
            state.deepLinkFetchPending = action.payload
        },
        // State mutations
        setTasks: (state, action: PayloadAction<Task[]>) => {
            state.byId = {}
            state.allIds = []
            action.payload.forEach(task => {
                state.byId[task.id] = task
                state.allIds.push(task.id)
            })
            state.optimistic = {}
            state.initialized = true
        },
        // Incremental snapshot update (from onSnapshot docChanges) — merges changed
        // tasks into byId instead of recreating the whole map, so unchanged tasks keep
        // their object identity and don't force every subscribed component to re-render.
        // Merge (not replace) preserves fields loaded separately (e.g. lazy description).
        upsertTasks: (state, action: PayloadAction<Task[]>) => {
            for (const task of action.payload) {
                // The eager task listeners read the task doc, which does NOT own `description` —
                // it lives in the separate task_descriptions collection, loaded by its own per-task
                // listener (subscribeDescriptionSaga) and the deep-link prefetch. Strip any
                // `description` off the snapshot before merging so a task-doc change (e.g. changing
                // the assignee) can never clobber the live, separately-loaded body. A legacy /
                // un-migrated task doc may still carry a stale `description` field; without this
                // strip, merging it wipes the description the user just typed (SCD-905).
                const { description: _ignored, ...incoming } = task as Task & { description?: unknown }
                state.byId[task.id] = { ...state.byId[task.id], ...incoming }
                if (!state.allIds.includes(task.id)) {
                    state.allIds.push(task.id)
                }
            }
            state.initialized = true
        },
        removeTaskIds: (state, action: PayloadAction<string[]>) => {
            if (action.payload.length === 0) return
            const ids = new Set(action.payload)
            for (const id of ids) delete state.byId[id]
            state.allIds = state.allIds.filter(id => !ids.has(id))
        },
        // Conditional removal for the split open/completed listeners. The open
        // listener (isCompleted == false) and the completed listener
        // (isCompleted == true) each report a task as "removed" when it leaves
        // their query — which happens both on a real delete AND on a completion
        // toggle that moves the task to the other listener's query. Without a
        // guard the two listeners race: the "removed" from one could land after
        // the "added" from the other and wrongly drop a task that only changed
        // state. So each listener removes an id only when the store's CURRENT
        // isCompleted still belongs to its own half — a transition (which the
        // other listener re-adds) is left alone, a real delete is removed.
        removeTaskIdsScoped: (state, action: PayloadAction<{ ids: string[]; completed: boolean }>) => {
            const { ids, completed } = action.payload
            if (ids.length === 0) return
            const toRemove = new Set(
                ids.filter(id => {
                    const t = state.byId[id]
                    if (!t) return false
                    return completed ? t.isCompleted === true : t.isCompleted !== true
                })
            )
            if (toRemove.size === 0) return
            for (const id of toRemove) delete state.byId[id]
            state.allIds = state.allIds.filter(id => !toRemove.has(id))
        },
        clearOptimistic: (state) => {
            state.optimistic = {}
        },
        addOptimistic: (state, action: PayloadAction<{ tempId: string; task: Task }>) => {
            state.optimistic[action.payload.tempId] = action.payload.task
        },
        removeOptimistic: (state, action: PayloadAction<string>) => {
            delete state.optimistic[action.payload]
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        setCompletedInitialized: (state, action: PayloadAction<boolean>) => {
            state.completedInitialized = action.payload
        },
        addTaskLocal: (state, action: PayloadAction<Task>) => {
            const task = action.payload
            state.byId[task.id] = task
            if (!state.allIds.includes(task.id)) {
                state.allIds.push(task.id)
            }
        },
        updateTaskLocal: (state, action: PayloadAction<{ taskId: string; data: Partial<Task> }>) => {
            const { taskId, data } = action.payload
            if (state.byId[taskId]) {
                Object.assign(state.byId[taskId], data)
            }
        },
        removeTaskLocal: (state, action: PayloadAction<string>) => {
            delete state.byId[action.payload]
            state.allIds = state.allIds.filter(id => id !== action.payload)
        },
        clearTasks: (state) => {
            state.byId = {}
            state.allIds = []
            state.optimistic = {}
            state.loading = true
            state.initialized = false
            state.completedInitialized = false
            state.deepLinkFetchPending = false
            state.error = null
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetState, () => initialState)
    }
})

export const {
    startListening,
    stopListening,
    createTaskSaga,
    createTasksSaga,
    updateTaskSaga,
    updateTaskDescriptionSaga,
    deleteTaskSaga,
    reorderTaskSaga,
    moveTaskToListSaga,
    toggleCompleteSaga,
    subscribeDescriptionSaga,
    unsubscribeDescriptionSaga,
    prefetchDescriptionSaga,
    fetchTaskByNumberSaga,
    setDeepLinkFetchPending,
    setTasks,
    upsertTasks,
    removeTaskIds,
    removeTaskIdsScoped,
    clearOptimistic,
    addOptimistic,
    removeOptimistic,
    addTaskLocal,
    updateTaskLocal,
    removeTaskLocal,
    setLoading,
    setError,
    setCompletedInitialized,
    clearTasks
} = tasksSlice.actions

export default tasksSlice.reducer
